/**
 * Inbox V3 Mapper Service
 *
 * This file contains mapper functions to transform inbox-v2 API responses
 * to inbox-v3 types. When the new API v3 comes, only these mappers need
 * to be updated, not the entire codebase.
 */

import {
  ConversationData,
  ContactDetails,
  ContactStats,
  TeamMember,
  ContactField,
  ContactInfo,
  ContactGroupMap,
  Message,
  MessageMedia,
  MessageCreator,
  MessagePagination,
} from '../types/inbox-v3.types'

import type {
  ApiConversation,
  ApiConversationMessage,
  ConversationPagination,
} from '../../../../../services/cellcast.conversations.types'

// ============================================================================
// Conversation Mappers
// ============================================================================

/**
 * Map a single inbox-v2 API conversation item to inbox-v3 ConversationData
 * API Response structure from inbox-v2:
 * {
 *   _id: string,
 *   receiver: string,
 *   message: string,
 *   createdAt: string,
 *   contacts: { _id, isInboxRead, isArchived, isFlagged, isTicked, isBlocked, isBlockedByUser },
 *   contactgroupmaps: { _id, fields: [{name, value}], tags: [] }
 * }
 */
export const mapV2ConversationToV3 = (v2Data: any): ConversationData => {
  // Handle null/undefined data
  if (!v2Data) {
    return createEmptyConversation()
  }

  const contacts: ContactInfo = {
    _id: v2Data.contacts?._id || '',
    isInboxRead: v2Data.contacts?.isInboxRead ?? true,
    isArchived: v2Data.contacts?.isArchived ?? false,
    isFlagged: v2Data.contacts?.isFlagged ?? false,
    isTicked: v2Data.contacts?.isTicked ?? false,
    isBlocked: v2Data.contacts?.isBlocked ?? false,
    isBlockedByUser: v2Data.contacts?.isBlockedByUser ?? false,
  }

  const fields: ContactField[] = Array.isArray(v2Data.contactgroupmaps?.fields)
    ? v2Data.contactgroupmaps.fields.map((f: any) => ({
        name: f?.name || '',
        value: f?.value || '',
      }))
    : []

  const contactgroupmaps: ContactGroupMap = {
    _id: v2Data.contactgroupmaps?._id || '',
    fields,
    tags: Array.isArray(v2Data.contactgroupmaps?.tags) ? v2Data.contactgroupmaps.tags : [],
  }

  return {
    _id: v2Data._id || '',
    receiver: v2Data.receiver || '',
    message: v2Data.message || '',
    createdAt: v2Data.createdAt || new Date().toISOString(),
    contacts,
    contactgroupmaps,
  }
}

/**
 * Map an array of inbox-v2 conversations to inbox-v3 ConversationData[]
 */
export const mapV2ConversationsToV3 = (v2List: any[]): ConversationData[] => {
  if (!Array.isArray(v2List)) {
    return []
  }
  return v2List.map(mapV2ConversationToV3)
}

/**
 * Create an empty conversation object (for error cases)
 */
const createEmptyConversation = (): ConversationData => ({
  _id: '',
  receiver: '',
  message: '',
  createdAt: new Date().toISOString(),
  contacts: {
    _id: '',
    isInboxRead: true,
    isArchived: false,
    isFlagged: false,
    isTicked: false,
    isBlocked: false,
    isBlockedByUser: false,
  },
  contactgroupmaps: {
    _id: '',
    fields: [],
    tags: [],
  },
})

// ============================================================================
// Contact Details Mappers
// ============================================================================

/**
 * Map inbox-v2 contact data to inbox-v3 ContactDetails
 * This is used for the right panel contact information
 */
export const mapV2ContactToV3 = (v2Contact: any): ContactDetails | null => {
  if (!v2Contact) {
    return null
  }

  // Extract first and last name from fields
  const fields = Array.isArray(v2Contact.fields) ? v2Contact.fields : []
  const firstName = fields.find((f: any) => f.name?.toLowerCase() === 'first name')?.value || ''
  const lastName = fields.find((f: any) => f.name?.toLowerCase() === 'last name')?.value || ''
  const email = fields.find((f: any) => f.name?.toLowerCase() === 'email')?.value || ''

  return {
    _id: v2Contact._id || '',
    number: v2Contact.receiver || v2Contact.number || '',
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`.trim() || v2Contact.receiver || '',
    email,
    description: v2Contact.description || '',
    tags: Array.isArray(v2Contact.tags)
      ? v2Contact.tags.map((tag: any) => ({
          name: typeof tag === 'string' ? tag : tag?.name || '',
          isHidden: tag?.isHidden ?? false,
          type: tag?.type || 'default',
        }))
      : [],
    fields: fields.map((f: any) => ({
      name: f?.name || '',
      value: f?.value || '',
    })),
    isBlocked: v2Contact.isBlocked ?? false,
    isBlockedByUser: v2Contact.isBlockedByUser ?? false,
    createdAt: v2Contact.createdAt || '',
    addedThrough: v2Contact.addedThrough || '',
    group: v2Contact.group
      ? {
          _id: v2Contact.group._id || '',
          fields: v2Contact.group.fields || [],
        }
      : undefined,
    contact: v2Contact.contact
      ? {
          _id: v2Contact.contact._id || '',
        }
      : undefined,
  }
}

/**
 * Extract contact details from a conversation
 * Used when we don't have a separate contact API call
 */
export const extractContactFromConversation = (
  conversation: ConversationData
): ContactDetails | null => {
  if (!conversation) {
    return null
  }

  const fields = conversation.contactgroupmaps?.fields || []
  const firstName = fields.find((f) => f.name?.toLowerCase() === 'first name')?.value || ''
  const lastName = fields.find((f) => f.name?.toLowerCase() === 'last name')?.value || ''
  const email = fields.find((f) => f.name?.toLowerCase() === 'email')?.value || ''

  return {
    _id: conversation.contacts?._id || '',
    number: conversation.receiver || '',
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`.trim() || conversation.receiver || '',
    email,
    tags: (conversation.contactgroupmaps?.tags || []).map((tag) => ({
      name: typeof tag === 'string' ? tag : '',
      isHidden: false,
      type: 'default',
    })),
    fields,
    isBlocked: conversation.contacts?.isBlocked ?? false,
    isBlockedByUser: conversation.contacts?.isBlockedByUser ?? false,
  }
}

// ============================================================================
// Team Member Mappers
// ============================================================================

/**
 * Map inbox-v2 team member/sub-account to inbox-v3 TeamMember
 */
export const mapV2TeamMemberToV3 = (v2Member: any): TeamMember => {
  return {
    _id: v2Member._id || v2Member.id || '',
    name:
      v2Member.name ||
      v2Member.fullName ||
      `${v2Member.firstName || ''} ${v2Member.lastName || ''}`.trim() ||
      'Unknown',
    email: v2Member.email || '',
    avatar: v2Member.avatar || v2Member.profileImage || '',
    assignedContacts: Array.isArray(v2Member.assignedContacts) ? v2Member.assignedContacts : [],
  }
}

/**
 * Map array of team members
 */
export const mapV2TeamMembersToV3 = (v2Members: any[]): TeamMember[] => {
  if (!Array.isArray(v2Members)) {
    return []
  }
  return v2Members.map(mapV2TeamMemberToV3)
}

// ============================================================================
// Stats Mappers
// ============================================================================

/**
 * Map contact stats from conversation API messageCount/messageTypeCount to inbox-v3 ContactStats
 *
 * Conversation API response structure:
 * {
 *   messageCount: { total, inbound, outbound, unread, bulk, quick },
 *   messageTypeCount: { sms, mms, log, task, note, system }
 * }
 */
export const mapConversationStatsToV3 = (
  messageCount: any,
  messageTypeCount: any
): ContactStats => {
  return {
    total: messageCount?.total || 0,
    inbound: messageCount?.inbound || 0,
    outbound: messageCount?.outbound || 0,
    unread: messageCount?.unread || 0,
    bulk: messageCount?.bulk || 0,
    quick: messageCount?.quick || 0,
    sms: messageTypeCount?.sms || 0,
    mms: messageTypeCount?.mms || 0,
  }
}

/**
 * Map contact information API response to inbox-v3 ContactDetails
 *
 * API Response structure (from contact/information endpoint):
 * {
 *   _id: string,
 *   fields: [{ name: string, value: string }],
 *   contact: { _id: string, isBlocked: boolean, isBlockedByUser: boolean, createdAt: string },
 *   tags: [{ _id: string, name: string, color: string, type: string }],
 *   group: { _id: string, fields: string[] }
 * }
 */
export const mapContactInformationToV3 = (
  v2ContactInfo: any,
  receiver?: string
): ContactDetails | null => {
  if (!v2ContactInfo) {
    return null
  }

  const fields = Array.isArray(v2ContactInfo.fields) ? v2ContactInfo.fields : []
  const firstName = fields.find((f: any) => f.name?.toLowerCase() === 'first name')?.value || ''
  const lastName = fields.find((f: any) => f.name?.toLowerCase() === 'last name')?.value || ''
  const email = fields.find((f: any) => f.name?.toLowerCase() === 'email')?.value || ''
  const phone =
    fields.find((f: any) => f.name?.toLowerCase() === 'phone' || f.name?.toLowerCase() === 'mobile')
      ?.value || ''

  return {
    _id: v2ContactInfo._id || v2ContactInfo.contact?._id || '',
    number: phone || receiver || '',
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`.trim() || phone || receiver || '',
    email,
    description: v2ContactInfo.description || '',
    tags: Array.isArray(v2ContactInfo.tags)
      ? v2ContactInfo.tags.map((tag: any) => ({
          _id: tag?._id || '',
          name: tag?.name || '',
          isHidden: tag?.isHidden ?? false,
          type: tag?.type || 'default',
        }))
      : [],
    fields: fields.map((f: any) => ({
      name: f?.name || '',
      value: f?.value || '',
    })),
    // isBlocked/isBlockedByUser can be at root level or nested under contact
    isBlocked: v2ContactInfo.isBlocked ?? v2ContactInfo.contact?.isBlocked ?? false,
    isBlockedByUser:
      v2ContactInfo.isBlockedByUser ?? v2ContactInfo.contact?.isBlockedByUser ?? false,
    createdAt: v2ContactInfo.contact?.createdAt || v2ContactInfo.createdAt || '',
    addedThrough: v2ContactInfo.addedThrough || v2ContactInfo.contact?.addedThrough || '',
    assignedTo: v2ContactInfo.assignedTo || null,
    group: v2ContactInfo.group
      ? {
          _id: v2ContactInfo.group._id || '',
          fields: v2ContactInfo.group.fields || [],
          mergeLabels: v2ContactInfo.group.mergeLabels || '[]',
        }
      : undefined,
    contact: v2ContactInfo.contact
      ? {
          _id: v2ContactInfo.contact._id || '',
        }
      : undefined,
  }
}

// ============================================================================
// Pagination Helpers
// ============================================================================

export interface PaginationInfo {
  current: number
  total: number
  totalPages: number
  limit: number
  hasNext: boolean
  hasPrev: boolean
}

/**
 * Extract pagination info from API response
 */
export const extractPaginationInfo = (apiResponse: any): PaginationInfo => {
  const pagination = apiResponse?.data?.pagination || apiResponse?.pagination || {}

  return {
    current: pagination.current || pagination.page || 1,
    total: pagination.total || pagination.totalItems || 0,
    totalPages: pagination.totalPages || 1,
    limit: pagination.limit || pagination.pageSize || 50,
    hasNext: pagination.hasNext ?? pagination.current < pagination.totalPages,
    hasPrev: pagination.hasPrev ?? pagination.current > 1,
  }
}

// ============================================================================
// Conversation API Mappers (/api/v1/conversations endpoint)
// ============================================================================

/**
 * Map API conversation to ConversationData
 *
 * API Response structure:
 * {
 *   _id: string,
 *   conversationName: string,
 *   participants: [{ number, name, email }],
 *   isRead: boolean,
 *   isFlagged: boolean,
 *   isArchived: boolean,
 *   isTicked: boolean,
 *   isBlocked: boolean,
 *   isBlockedByUser: boolean,
 *   lastMessage: { rawMessage, timestamp, ... },
 *   lastMessageAt: string,
 *   ...
 * }
 */
export const mapApiConversation = (apiConv: ApiConversation): ConversationData => {
  if (!apiConv) {
    return createEmptyConversation()
  }

  // Get the first participant (for single conversations)
  const participant = apiConv.participants?.[0]

  // Build fields from participant data
  const fields: ContactField[] = []
  if (participant?.name) {
    const nameParts = participant.name.split(' ')
    fields.push({name: 'First Name', value: nameParts[0] || ''})
    fields.push({name: 'Last Name', value: nameParts.slice(1).join(' ') || ''})
  }
  if (participant?.email) {
    fields.push({name: 'Email', value: participant.email})
  }

  // Use participant.refId as the contact ID (this is what assignedContacts uses)
  // Fallback to conversation _id if refId is not available
  const contactId = participant?.refId || apiConv._id

  const contacts: ContactInfo = {
    _id: contactId,
    isInboxRead: apiConv.isRead ?? true,
    isArchived: apiConv.isArchived ?? false,
    isFlagged: apiConv.isFlagged ?? false,
    isTicked: apiConv.isTicked ?? false,
    isBlocked: apiConv.isBlocked ?? false,
    isBlockedByUser: apiConv.isBlockedByUser ?? false,
  }

  const contactgroupmaps: ContactGroupMap = {
    _id: contactId,
    fields,
    tags: [], // Tags not included in basic listing, fetched separately
  }

  return {
    _id: apiConv._id,
    receiver: participant?.number || '',
    message: apiConv.lastMessage?.rawMessage || '',
    createdAt: apiConv.lastMessageAt || apiConv.createdAt,
    contacts,
    contactgroupmaps,
    // Include participant reference for fetching contact details/stats
    participantRefId: participant?.refId,
    participantRefType: participant?.refType,
  }
}

/**
 * Map array of API conversations to ConversationData[]
 */
export const mapApiConversations = (apiConvs: ApiConversation[]): ConversationData[] => {
  if (!Array.isArray(apiConvs)) {
    return []
  }
  return apiConvs.map(mapApiConversation)
}

/**
 * Map API message to Message type
 *
 * API Message structure:
 * {
 *   _id: string,
 *   type: 'sms' | 'mms' | ...,
 *   rawMessage: string,
 *   direction: 'inbound' | 'outbound',
 *   status: string,
 *   sender: string,
 *   receiver: string,
 *   createdAt: string,
 *   media: [],
 *   metadata: { campaignType, ... },
 *   ...
 * }
 */
/**
 * Extract sender string from sender field
 * API may return sender as string or object with {isSystem, number, name}
 */
const extractSenderString = (sender: any): string => {
  if (!sender) return ''
  if (typeof sender === 'string') return sender
  // Handle object format: {isSystem, number, name}
  if (typeof sender === 'object') {
    return sender.number || sender.name || ''
  }
  return String(sender)
}

/**
 * Extract sender name from sender field
 * API returns sender as object with {isSystem, number, name}
 */
const extractSenderName = (sender: any): string => {
  if (!sender) return ''
  if (typeof sender === 'object' && sender.name) {
    return sender.name
  }
  return ''
}

export const mapApiMessage = (apiMsg: ApiConversationMessage): Message => {
  if (!apiMsg) {
    return createEmptyMessage()
  }

  // Determine campaign_type from direction and metadata
  const isInbound = apiMsg.direction === 'inbound'
  const campaignType = isInbound ? 'reply' : apiMsg.metadata?.campaignType || 'notification'

  // Extract sender name from sender object for outbound messages
  const senderName = extractSenderName(apiMsg.sender)

  // Extract createdBy info - handle both string (ID only) and object (populated) formats
  let createdByInfo: {_id: string; name: string; avatar: string} | undefined
  if (apiMsg.createdBy) {
    if (typeof apiMsg.createdBy === 'string') {
      // Unpopulated - just the ID
      createdByInfo = {_id: apiMsg.createdBy, name: senderName || 'System', avatar: ''}
    } else {
      // Populated - object with user details
      const createdByObj = apiMsg.createdBy
      createdByInfo = {
        _id: createdByObj._id || '',
        name: createdByObj.name || senderName || 'System',
        avatar: createdByObj.avatar || createdByObj.profile_pic || '',
      }
    }
  }

  return {
    _id: apiMsg._id,
    sender: extractSenderString(apiMsg.sender),
    receiver: extractSenderString(apiMsg.receiver),
    message: apiMsg.rawMessage || apiMsg.encryptedMessage || '',
    createdAt: apiMsg.createdAt || new Date().toISOString(),
    status: apiMsg.status || 'sent',
    campaign_type: campaignType,
    notification_type: apiMsg.subType || '',
    medium: apiMsg.type || 'sms',
    isSMS: apiMsg.type === 'sms',
    isMMS: apiMsg.type === 'mms' || apiMsg.hasMedia,
    isReplied: isInbound,
    isRead: apiMsg.isRead ?? false,
    media: mapMediaArray(apiMsg.media || []),
    customerResponseMedia: isInbound ? mapMediaArray(apiMsg.media || []) : [],
    mediaUrl: apiMsg.urls || [],
    createdBy: createdByInfo,
    scheduleAt: apiMsg.metadata?.sentAt || undefined,
    send_time: apiMsg.metadata?.sentAt || undefined,
    send_state: null,
    gateway_count: apiMsg.metadata?.smsCount || 1,
    messageGateway: apiMsg.metadata?.gateway || '',
    longUrl: [],
  }
}

/**
 * Map array of API messages to Message[]
 */
export const mapApiMessages = (apiMsgs: ApiConversationMessage[]): Message[] => {
  if (!Array.isArray(apiMsgs)) {
    return []
  }
  return apiMsgs.map(mapApiMessage)
}

/**
 * Map API pagination to MessagePagination
 */
export const mapApiPagination = (pagination: ConversationPagination): MessagePagination => {
  return {
    current: pagination.page || 1,
    total: pagination.totalDocs || 0,
    totalPages: pagination.totalPages || 1,
    limit: pagination.limit || 20,
    hasMore: pagination.hasNextPage ?? false,
  }
}

/**
 * Extract contact details from API conversation
 */
export const extractContactFromApiConversation = (
  apiConv: ApiConversation
): ContactDetails | null => {
  if (!apiConv) {
    return null
  }

  const participant = apiConv.participants?.[0]
  if (!participant) {
    return null
  }

  const nameParts = (participant.name || '').split(' ')
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''

  return {
    _id: apiConv._id,
    number: participant.number || '',
    firstName,
    lastName,
    fullName: participant.name || participant.number || '',
    email: participant.email || '',
    tags: [],
    fields: [
      {name: 'First Name', value: firstName},
      {name: 'Last Name', value: lastName},
      {name: 'Email', value: participant.email || ''},
    ],
    isBlocked: apiConv.isBlocked ?? false,
    isBlockedByUser: apiConv.isBlockedByUser ?? false,
    createdAt: apiConv.createdAt || '',
  }
}

// ============================================================================
// Message Mappers
// ============================================================================

/**
 * Map media array from API response to MessageMedia[]
 */
const mapMediaArray = (mediaArray: any[]): MessageMedia[] => {
  if (!Array.isArray(mediaArray)) {
    return []
  }
  return mediaArray.map((media) => ({
    url: typeof media === 'string' ? media : media?.url || '',
    type: media?.type || 'image',
    name: media?.name || '',
  }))
}

/**
 * Map creator info from API response
 */
const mapCreator = (creator: any): MessageCreator | undefined => {
  if (!creator) {
    return undefined
  }
  return {
    _id: creator._id || '',
    name: creator.name || creator.fullName || 'Unknown',
    avatar: creator.avatar || creator.profileImage || '',
    email: creator.email || '',
  }
}

/**
 * Map a single message from inbox-v2 API to inbox-v3 Message type
 *
 * API Response structure (from gateway endpoint):
 * {
 *   _id: string,
 *   sender: string,
 *   receiver: string,
 *   message: string,
 *   createdAt: string,
 *   status: string,
 *   campaign_type: string,
 *   media: [{url: string}],
 *   customerResponseMedia: [{url: string}],
 *   createdBy: { _id, name, avatar },
 *   scheduleAt?: string,
 *   ...
 * }
 */
export const mapV2MessageToV3 = (v2Message: any): Message => {
  if (!v2Message) {
    return createEmptyMessage()
  }

  return {
    _id: v2Message._id || '',
    sender: extractSenderString(v2Message.sender),
    receiver: extractSenderString(v2Message.receiver),
    message: v2Message.message || '',
    createdAt: v2Message.createdAt || new Date().toISOString(),
    status: v2Message.status || 'sent',
    campaign_type: v2Message.campaign_type || '',
    notification_type: v2Message.notification_type || '',
    medium: v2Message.medium || '',
    isSMS: v2Message.isSMS ?? true,
    isMMS: v2Message.isMMS ?? false,
    isReplied: v2Message.isReplied ?? false,
    isRead: v2Message.isRead ?? false,
    media: mapMediaArray(v2Message.media),
    customerResponseMedia: mapMediaArray(v2Message.customerResponseMedia),
    mediaUrl: Array.isArray(v2Message.mediaUrl) ? v2Message.mediaUrl : [],
    createdBy: mapCreator(v2Message.createdBy),
    scheduleAt: v2Message.scheduleAt || undefined,
    send_time: v2Message.send_time || undefined,
    send_state: v2Message.send_state || null,
    gateway_count: v2Message.gateway_count || 0,
    messageGateway: v2Message.messageGateway || '',
    longUrl: Array.isArray(v2Message.longUrl) ? v2Message.longUrl : [],
  }
}

/**
 * Map an array of messages from inbox-v2 API
 */
export const mapV2MessagesToV3 = (v2Messages: any[]): Message[] => {
  if (!Array.isArray(v2Messages)) {
    return []
  }
  return v2Messages.map(mapV2MessageToV3)
}

/**
 * Create an empty message object (for error cases)
 */
const createEmptyMessage = (): Message => ({
  _id: '',
  sender: '',
  receiver: '',
  message: '',
  createdAt: new Date().toISOString(),
  status: 'sent',
  media: [],
  customerResponseMedia: [],
})

/**
 * Extract pagination info from messages API response
 */
export const extractMessagePagination = (apiResponse: any): MessagePagination => {
  const data = apiResponse?.data || apiResponse || {}
  const pagination = data.pagination || {}
  const items = data.items || []

  const current = pagination.current || pagination.page || 1
  const total = pagination.total || pagination.totalItems || items.length
  const limit = pagination.limit || pagination.pageSize || 20
  const totalPages = pagination.totalPages || Math.ceil(total / limit) || 1

  return {
    current,
    total,
    totalPages,
    limit,
    hasMore: current < totalPages,
  }
}

// ============================================================================
// Edit Contact Mappers
// ============================================================================

/**
 * Contact form data structure for editing
 */
export interface EditContactFormData {
  number: string
  firstName?: string
  lastName?: string
  email?: string
  [key: string]: string | undefined // Dynamic fields
}

/**
 * Edit contact API payload structure
 */
export interface EditContactPayload {
  id: string
  groupId?: string
  data: Record<string, string>
  fields?: string[]
  mergeLabels?: string
  tagIds?: string[]
}

/**
 * Map ContactDetails to EditContactFormData for form initialization
 */
export const mapContactDetailsToFormData = (
  contact: ContactDetails | null
): EditContactFormData => {
  if (!contact) {
    return {number: ''}
  }

  const formData: EditContactFormData = {
    number: contact.number || '',
    firstName: contact.firstName || '',
    lastName: contact.lastName || '',
    email: contact.email || '',
  }

  // Add dynamic fields from contact.fields
  if (Array.isArray(contact.fields)) {
    contact.fields.forEach((field) => {
      if (field.name && !['number', 'firstName', 'lastName', 'email'].includes(field.name)) {
        formData[field.name] = field.value || ''
      }
    })
  }

  return formData
}

/**
 * Map form data to API payload format for V2 API
 * PUT group/updatequickcontact/{groupId}/{contactId}
 */
export const mapFormDataToV2Payload = (
  formData: EditContactFormData,
  contactId: string,
  groupId: string,
  existingFields: string[] = [],
  mergeLabels: string = '[]',
  newFields: Array<{label: string; value: string}> = []
): EditContactPayload => {
  // Build data object from form data
  const data: Record<string, string> = {}
  Object.entries(formData).forEach(([key, value]) => {
    if (value !== undefined) {
      data[key] = value
    }
  })

  // Add new fields to data
  newFields.forEach((field) => {
    data[field.label] = field.value
  })

  // Merge existing fields with new field labels
  const newFieldLabels = newFields.map((f) => f.label)
  const allFields = [...existingFields, ...newFieldLabels]

  // Parse and update merge labels
  let parsedMergeLabels: Array<{oldLabel: string; newLabel: string}> = []
  try {
    parsedMergeLabels = JSON.parse(mergeLabels || '[]')
  } catch {
    parsedMergeLabels = []
  }
  const newMergeLabels = newFields.map((f) => ({oldLabel: f.label, newLabel: f.label}))
  const allMergeLabels = [...parsedMergeLabels, ...newMergeLabels]

  return {
    id: contactId,
    groupId,
    data,
    fields: allFields,
    mergeLabels: JSON.stringify(allMergeLabels),
  }
}

/**
 * Map form data to API payload format for V3 API
 * POST contact with action: 'update_contact'
 */
export const mapFormDataToV3Payload = (
  formData: EditContactFormData,
  contactId: string,
  tagIds: string[] = [],
  newFields: Array<{label: string; value: string}> = []
): any => {
  // Build data object from form data
  const data: Record<string, string> = {}
  Object.entries(formData).forEach(([key, value]) => {
    if (value !== undefined) {
      data[key] = value
    }
  })

  // Add new fields to data
  newFields.forEach((field) => {
    data[field.label] = field.value
  })

  return {
    id: contactId,
    ...data,
    tag_ids: tagIds,
  }
}

/**
 * Get field names from ContactDetails for form display
 */
export const getContactFieldNames = (contact: ContactDetails | null): string[] => {
  if (!contact) return []

  const standardFields = ['firstName', 'lastName', 'email']
  const dynamicFields = contact.fields?.map((f) => f.name) || []

  // Filter out duplicates and return unique field names
  const allFields = [...standardFields, ...dynamicFields]
  return Array.from(new Set(allFields))
}

/**
 * Map API error response to user-friendly message
 */
export const mapContactApiError = (error: any): string => {
  if (error?.data?.message) {
    return error.data.message
  }
  if (error?.message) {
    return error.message
  }
  return 'Failed to update contact. Please try again.'
}

// ============================================================================
// URL Query Parameter Mappers
// ============================================================================

/**
 * Receiver param type - can be phone number or conversation ID
 */
export type ReceiverParamType = 'number' | 'conversationId'

/**
 * Check if a string looks like a phone number
 * Phone numbers typically start with + or digits and contain only digits, +, -, spaces
 */
const isPhoneNumber = (value: string): boolean => {
  // Phone number pattern: starts with + or digit, contains digits, may have +, -, spaces, ()
  const phonePattern = /^[\+]?[\d\s\-\(\)]+$/
  // Must have at least 8 digits to be a valid phone number
  const digitCount = (value.match(/\d/g) || []).length
  return phonePattern.test(value) && digitCount >= 8
}

/**
 * Check if a string looks like a MongoDB ObjectId (conversation ID)
 * ObjectIds are 24 character hex strings
 */
const isObjectId = (value: string): boolean => {
  return /^[a-f\d]{24}$/i.test(value)
}

/**
 * Detect the type of receiver param
 *
 * @param receiverParam - The receiver value from URL
 * @returns The detected type ('number' or 'conversationId')
 */
export const detectReceiverParamType = (receiverParam: string | null): ReceiverParamType | null => {
  if (!receiverParam) return null

  // Check if it looks like a MongoDB ObjectId (conversation ID)
  if (isObjectId(receiverParam)) {
    return 'conversationId'
  }

  // Check if it looks like a phone number
  if (isPhoneNumber(receiverParam)) {
    return 'number'
  }

  // Default to phone number for backward compatibility
  return 'number'
}

/**
 * Get the receiver identifier for URL query params
 *
 * V2 API: Uses contact phone number as receiver
 * V3 API: Uses conversation ID as receiver (but also supports phone number for backward compatibility)
 *
 * @param conversation - The conversation data
 * @param preferConversationId - Whether to prefer conversation ID over phone number
 * @returns The receiver identifier for URL
 */
export const getReceiverParam = (
  conversation: ConversationData | null,
  preferConversationId: boolean = false
): string | null => {
  if (!conversation) return null

  if (preferConversationId && conversation._id) {
    // V3 API: Prefer conversation ID
    return conversation._id
  }

  // V2 API or fallback: Use contact phone number
  return conversation.receiver || null
}

/**
 * Parse receiver from URL query params
 *
 * Automatically detects whether the param is a phone number or conversation ID
 *
 * @param receiverParam - The receiver value from URL
 */
export const parseReceiverParam = (
  receiverParam: string | null
): {type: ReceiverParamType; value: string} | null => {
  if (!receiverParam) return null

  const type = detectReceiverParamType(receiverParam)
  if (!type) return null

  return {type, value: receiverParam}
}

/**
 * Find conversation by receiver param
 *
 * Handles both phone number and conversation ID formats.
 * V3 inbox supports both for backward compatibility with V2 links.
 *
 * @param conversations - List of conversations to search
 * @param receiverParam - The receiver value from URL
 */
export const findConversationByReceiver = (
  conversations: ConversationData[],
  receiverParam: string | null
): ConversationData | null => {
  if (!receiverParam || !conversations.length) return null

  const parsed = parseReceiverParam(receiverParam)
  if (!parsed) return null

  if (parsed.type === 'conversationId') {
    // Find by conversation ID
    return conversations.find((c) => c._id === parsed.value) || null
  }

  // Find by phone number
  return conversations.find((c) => c.receiver === parsed.value) || null
}
